import React, { createContext, lazy, useContext, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";

const AccessContext = createContext();

export const AccessProvider = ({ children }) => {
  const { userAccess } = useSelector((state) => state.user);

  console.log(userAccess);

  useEffect(() => {
    if (!userAccess || !Array.isArray(userAccess)) return;

    const hasHighAccess = userAccess.some((role) => role.direct_login === true);

    const collectAllowedIds = () => {
      const allowedIds = [];
      userAccess.forEach((role) => {
        if (role.custom_access?.pages_id) {
          Object.values(role.custom_access.pages_id).forEach((moduleIds) => {
            if (Array.isArray(moduleIds)) {
              allowedIds.push(...moduleIds.filter((id) => id !== null));
            }
          });
        }
      });
      return [...new Set(allowedIds)];
    };

    const applyAccessControl = () => {
      const allowed = collectAllowedIds();
      const elements = document.querySelectorAll('[id^="access_"]');
      elements.forEach((el) => {
        if (hasHighAccess || allowed.includes(el.id)) {
          el.style.display = "";
        } else {
          el.style.display = "none";
        }
      });
    };

    applyAccessControl();

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            if (hasHighAccess) {
              if (node.id && node.id.startsWith("access_")) {
                node.style.display = "";
              }
              const childElements = node.querySelectorAll('[id^="access_"]');
              childElements.forEach((child) => {
                child.style.display = "";
              });
              return;
            }

            const uniqueAllowedIds = collectAllowedIds();

            if (node.id && node.id.startsWith("access_")) {
              if (uniqueAllowedIds.includes(node.id)) {
                node.style.display = "";
              } else {
                node.style.display = "none";
                console.log(
                  `Hiding new element: ${node.id} (not in allowed list)`
                );
              }
            }

            const childElements = node.querySelectorAll('[id^="access_"]');
            childElements.forEach((child) => {
              if (uniqueAllowedIds.includes(child.id)) {
                child.style.display = "";
              } else {
                child.style.display = "none";
                console.log(
                  `Hiding new child element: ${child.id} (not in allowed list)`
                );
              }
            });
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, [userAccess]);

  const value = {
    userAccess,
    canNavigate: (path) => {
      if (!userAccess || !Array.isArray(userAccess)) return false;
      if (
        userAccess.some(
          (role) => role.direct_login && role.direct_login === true
        )
      )
        return true;
      return userAccess.some((role) =>
        role.custom_access?.modules_path?.includes(path)
      );
    },

    hasAccess: (permission) => {
      if (!userAccess || !Array.isArray(userAccess)) return false;
      if (
        userAccess.some(
          (role) => role.direct_login && role.direct_login === true
        )
      )
        return true;
      return userAccess.some((role) =>
        role.custom_access?.modules_id?.includes(permission)
      );
    },

    isHighAccess: () => {
      if (!userAccess || !Array.isArray(userAccess)) return false;
      return userAccess.some((role) => role.direct_login === true);
    },

    hasModuleAccess: (moduleId) => {
      if (!userAccess || !Array.isArray(userAccess)) return false;

      if (
        userAccess.some(
          (role) => role.direct_login && role.direct_login === true
        )
      )
        return true;

      return userAccess.some((role) =>
        role.custom_access?.modules_id?.includes(moduleId)
      );
    },

    hasPathAccess: (path) => {
      if (!userAccess || !Array.isArray(userAccess)) return false;

      if (
        userAccess.some(
          (role) => role.direct_login && role.direct_login === true
        )
      )
        return true;

      return userAccess.some((role) =>
        role.custom_access?.modules_path?.includes(path)
      );
    },

    getAllowedPaths: () => {
      if (!userAccess || !Array.isArray(userAccess)) return [];

      if (
        userAccess.some(
          (role) => role.direct_login && role.direct_login === true
        )
      )
        return true;

      const allPaths = [];
      userAccess.forEach((role) => {
        if (role.custom_access?.modules_path) {
          allPaths.push(...role.custom_access.modules_path);
        }
      });
      return [...new Set(allPaths)]; // Remove duplicates
    },
    getAllowedModules: () => {
      if (!userAccess || !Array.isArray(userAccess)) return [];

      if (
        userAccess.some(
          (role) => role.direct_login && role.direct_login === true
        )
      )
        return true;

      const allModules = [];
      userAccess.forEach((role) => {
        if (role.custom_access?.modules_id) {
          allModules.push(
            ...role.custom_access.modules_id.filter((id) => id !== null)
          );
        }
      });
      return [...new Set(allModules)]; // Remove duplicates
    },
    getAllowedPageIds: () => {
      if (!userAccess || !Array.isArray(userAccess)) return [];

      if (userAccess.some((role) => role.direct_login === true)) return ["*"];

      const allowedIds = [];
      userAccess.forEach((role) => {
        if (role.custom_access?.pages_id) {
          Object.values(role.custom_access.pages_id).forEach((moduleIds) => {
            if (Array.isArray(moduleIds)) {
              allowedIds.push(...moduleIds.filter((id) => id !== null));
            }
          });
        }
      });
      return [...new Set(allowedIds)]; // Remove duplicates
    },
    hasPageAccess: (pageId) => {
      if (!userAccess || !Array.isArray(userAccess)) return false;

      if (userAccess.some((role) => role.direct_login === true)) return true;

      return userAccess.some((role) => {
        if (role.custom_access?.pages_id) {
          return Object.values(role.custom_access.pages_id).some(
            (moduleIds) =>
              Array.isArray(moduleIds) && moduleIds.includes(pageId)
          );
        }
        return false;
      });
    },

    hasAnyAccess: (items) => {
      if (!userAccess || !Array.isArray(userAccess)) return false;

      if (userAccess.some((role) => role.direct_login === true)) return true;

      // Get all allowed items from all roles
      const allAllowedPaths = [];
      const allAllowedModules = [];
      const allAllowedPageIds = [];

      userAccess.forEach((role) => {
        if (role.custom_access?.modules_path) {
          allAllowedPaths.push(...role.custom_access.modules_path);
        }
        if (role.custom_access?.modules_id) {
          allAllowedModules.push(
            ...role.custom_access.modules_id.filter((id) => id !== null)
          );
        }
        if (role.custom_access?.pages_id) {
          Object.values(role.custom_access.pages_id).forEach((moduleIds) => {
            if (Array.isArray(moduleIds)) {
              allAllowedPageIds.push(...moduleIds.filter((id) => id !== null));
            }
          });
        }
      });

      return items.some(
        (item) =>
          allAllowedPaths.includes(item) ||
          allAllowedModules.includes(item) ||
          allAllowedPageIds.includes(item)
      );
    },

    // New method to check if an element should be hidden
    shouldHideElement: (elementId) => {
      if (!userAccess || !Array.isArray(userAccess)) return true;

      if (userAccess.some((role) => role.direct_login === true)) return false;

      const allowedIds = [];
      userAccess.forEach((role) => {
        if (role.custom_access?.pages_id) {
          Object.values(role.custom_access.pages_id).forEach((moduleIds) => {
            if (Array.isArray(moduleIds)) {
              allowedIds.push(...moduleIds.filter((id) => id !== null));
            }
          });
        }
      });

      const uniqueAllowedIds = [...new Set(allowedIds)];
      return !uniqueAllowedIds.includes(elementId);
    },
  };

  return (
    <AccessContext.Provider value={value}>{children}</AccessContext.Provider>
  );
};

export const useAccess = () => {
  const context = useContext(AccessContext);
  if (!context) {
    throw new Error("useAccess must be used within an AccessProvider");
  }
  return context;
};

export const noAccessProvide = [
  {
    role_name: "default access",
    description:
      "this access will use when user is invited but don't access is provided",
    custom_access: {
      pages_id: {
        task_module: [
          "access_new_task_btn",
          "access_new_task_edit_btn",
          "access_new_task_del_btn",
          "access_assign_to_other",
          "access_privat_task_input",
          "access_add_task_list",
          "access_edit_task_list",
          "access_delete_task_list",
          "access_add_task_type",
          "access_edit_task_type",
          "access_delete_task_type",
          "access_task_feed_comment",
        ],
        matters_module: [
          "access_matters_create",
          "access_matters_edit",
          "access_matters_delete",
          "access_matters_close",
          "access_matters_archive",
          "access_matter_stages_create",
          "access_matter_stages_edit",
          "access_matter_stages_delete",
          "access_matter_templates_create",
          "access_matter_templates_edit",
          "access_matter_templates_delete",
        ],
        calendar_module: [
          "access_calendar_create",
          "access_calendar_edit",
          "access_calendar_delete",
          "access_calendar_manage_others",
        ],
        contacts_module: [
          "access_contacts_create",
          "access_contacts_edit",
          "access_contacts_delete",
          "access_contacts_export",
          "access_contacts_import",
        ],
        documents_module: [
          "access_documents_upload",
          "access_documents_download",
          "access_documents_edit",
          "access_documents_delete",
          "access_documents_share",
          "access_document_templates_create",
          "access_document_templates_edit",
          "access_document_templates_delete",
          "access_document_categories_create",
          "access_document_categories_edit",
          "access_document_categories_delete",
        ],
        activities_module: [
          "access_activities_create",
          "access_activities_edit",
          "access_activities_delete",
          "access_activities_log_time",
          "access_activities_add_exp",
          "access_activities_add_exp_categories",
        ],
        integrations_module: [
          "access_integrations_configure",
          "access_integrations_connect",
          "access_integrations_disconnect",
        ],
        custom_fields_module: [
          "access_custom_fields_create",
          "access_custom_fields_edit",
          "access_custom_fields_delete",
        ],
        communications_module: [
          "access_communications_send",
          "access_communications_email",
          "access_communications_sms",
        ],
      },
      modules_id: [
        "sidebar_activities",
        "sidebar_all_matters",
        "sidebar_all_tasks",
        "sidebar_calendar",
        "sidebar_communications",
        "sidebar_contacts",
        "sidebar_custom_fields",
        "sidebar_dashboard",
        "sidebar_document_categories",
        "sidebar_document_templates",
        "sidebar_documents",
        "sidebar_documents_main",
        "sidebar_integrations",
        "sidebar_matter_stages",
        "sidebar_matter_templates",
        "sidebar_matters",
        "sidebar_task_feed",
        "sidebar_task_lists",
        "sidebar_task_type",
        "sidebar_tasks",
      ],
      modules_path: [
        "/dashboard",
        "/dashboard/activities",
        "/dashboard/calendars",
        "/dashboard/communications",
        "/dashboard/contact-details",
        "/dashboard/custom_fields",
        "/dashboard/documents",
        "/dashboard/documents/categories",
        "/dashboard/documents/templates",
        "/dashboard/matter",
        "/dashboard/matter-stages",
        "/dashboard/matter-template",
        "/dashboard/task/lists",
        "/dashboard/task/type",
        "/dashboard/tasks",
        "/dashboard/tasks/feeds",
      ],
    },
  },
];
